/**
 * SoleadLab 2.0 — Scoring Types (D2.4)
 *
 * TypeScript interfaces matching the D2.3 scoring API response shapes.
 * Three-state model: scored / not_scored / no_state (§7.4).
 * All values are backend outputs only — no frontend derivation.
 */

// ─── Score entry types (three-state model) ─────────────────────────────────

export interface ScoredEntry {
  status: 'scored';
  entity_type: 'asset' | 'site';
  entity_id: string;
  score_type: string;
  total_score: number;
  confidence_score: number;
  breakdown_json: BreakdownJSON;
  score_snapshot_id: string;
  created_at: string;
}

export interface NotScoredEntry {
  status: 'not_scored';
  entity_type: 'asset' | 'site';
  entity_id: string;
  score_type: string;
  blocked_reason: string;
  blocked_detail: string;
}

export interface NoStateEntry {
  status: 'no_state';
  entity_type: 'asset' | 'site';
  entity_id: string;
  message: string;
}

export type ScoreEntry = ScoredEntry | NotScoredEntry | NoStateEntry;

// ─── Breakdown JSON shape (from scoring engines) ────────────────────────────

export interface BreakdownComponent {
  component_type: string;
  eligible: boolean;
  raw_value: number | null;
  normalized_value: number;
  base_weight: number;
  effective_weight: number;
  weighted_contribution: number;
  score_component_id: string;
}

export interface IneligibleComponent {
  component_type: string;
  base_weight: number;
  reason: string;
}

export interface BreakdownJSON {
  scoring_version: string;
  state_snapshot_id: string;
  state_version: string;
  eligibility: {
    state_exists: boolean;
    confidence_above_threshold: boolean;
    quality_above_threshold: boolean;
    entity_type_valid: boolean;
    required_dimensions_present: boolean;
    min_component_coverage_met: boolean;
    all_passed: boolean;
    data_quality_score: number;
    confidence_score: number;
  };
  components: BreakdownComponent[];
  ineligible_components: IneligibleComponent[];
  confidence_derivation: {
    data_quality_score: number;
    component_coverage_fraction: number;
    formula: string;
    result: number;
  };
  total_score_verification: {
    sum_of_contributions: number;
    total_score: number;
    sum_of_effective_weights: number;
  };
}

// ─── API response shapes ────────────────────────────────────────────────────

export interface ListScoresResponse {
  site_scores: Record<string, ScoreEntry>;
  assets: Array<{
    asset_id: string;
    scores: Record<string, ScoredEntry | NotScoredEntry>;
  }>;
}

export interface ScoreHistoryItem {
  score_snapshot_id: string;
  snapshot_id: string;
  total_score: number;
  confidence_score: number;
  breakdown_json: BreakdownJSON;
  created_at: string;
}

export interface ScoreHistoryResponse {
  entity_type: string;
  entity_id: string;
  score_type: string;
  items: ScoreHistoryItem[];
  total: number;
  limit: number;
  offset: number;
}

export interface ScoreComponentRow {
  id: string;
  component_type: string;
  raw_value: number | null;
  normalized_value: number | null;
  weight: number;
  confidence_score: number;
  explanation: unknown;
}

export interface ScoreComponentsResponse {
  score_snapshot_id: string;
  entity_type: string;
  entity_id: string;
  score_type: string;
  total_score: number;
  confidence_score: number;
  components: ScoreComponentRow[];
}

// ─── Score type labels ──────────────────────────────────────────────────────

export const SCORE_TYPE_LABELS: Record<string, string> = {
  site_health: 'Site Health',
  site_risk: 'Site Risk',
  performance: 'Performance',
  asset_health: 'Asset Health',
};

export const SITE_SCORE_TYPES = ['site_health', 'site_risk', 'performance'] as const;
export const ASSET_SCORE_TYPES = ['asset_health', 'performance'] as const;
